﻿import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  Calendar,
  ClipboardList,
  CreditCard,
  Database,
  Eye,
  FileText,
  Headphones,
  Home,
  KeyRound,
  ListPlus,
  ListTodo,
  Lock,
  MonitorSmartphone,
  Package,
  Plane,
  Receipt,
  Repeat,
  Rocket,
  Settings,
  Shield,
  Target,
  User,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { MODULES, PERMISSIONS, type ModuleCode } from '@pms/shared';

export type NavStatus = 'ready' | 'planned';

export interface NavLeaf {
  label: string;
  path: string;
  icon?: LucideIcon;
  module?: ModuleCode | null;
  status?: NavStatus;
  requiredPermission?: string;
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  status?: NavStatus;
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export interface NavSection {
  label: string;
  items: NavItem[];
}

export function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

/**
 * Sidebar navigation grouped into functional sections.
 * Items with `status: 'planned'` route to ComingSoonPage until implemented.
 *
 * Visibility is gated by `module`: an item is shown only if the user's
 * `user_modules` contains the module code. SUPER_ADMIN sees everything.
 * Group visibility is computed from children.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: Home, module: MODULES.DASHBOARD, status: 'ready' },
    ],
  },
  {
    label: 'Productivity',
    items: [
      { label: 'Task', path: '/tasks', icon: ListTodo, module: MODULES.TASK, status: 'ready', requiredPermission: PERMISSIONS.TASK_VIEW_OWN },
      { label: 'Additional Task', path: '/additional-task', icon: ListPlus, module: MODULES.ADDITIONAL_TASK, status: 'ready', requiredPermission: PERMISSIONS.ADDITIONAL_TASK_VIEW_OWN },
      { label: 'Goals', path: '/goals', icon: Target, module: MODULES.GOALS, status: 'ready', requiredPermission: PERMISSIONS.GOALS_VIEW_OWN },
      { label: 'Calendar', path: '/calendar', icon: Calendar, module: MODULES.CALENDAR, status: 'ready', requiredPermission: PERMISSIONS.CALENDAR_VIEW_OWN },
      { label: 'SOP', path: '/sop', icon: BookOpen, module: MODULES.SOP, status: 'ready', requiredPermission: PERMISSIONS.SOP_VIEW },
      { label: 'Notice', path: '/notice', icon: Bell, module: MODULES.NOTICE, status: 'ready', requiredPermission: PERMISSIONS.NOTICE_VIEW },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'HRMS',
        icon: Users,
        status: 'ready',
        children: [
          { label: 'Staff Management', path: '/hrms/staff/list', icon: Users, module: MODULES.HRMS_STAFF, status: 'ready', requiredPermission: PERMISSIONS.STAFF_VIEW_TEAM },
          { label: 'Organization', path: '/hrms/organization', icon: Building2, module: MODULES.HRMS_ORGANIZATION, status: 'ready', requiredPermission: PERMISSIONS.ORG_VIEW },
          { label: 'HRMS Config', path: '/hrms/config', icon: Database, module: MODULES.HRMS_ORGANIZATION, status: 'ready', requiredPermission: PERMISSIONS.DEPARTMENTS_VIEW },
          { label: 'Manage SOP', path: '/hrms/sop', icon: BookOpen, module: MODULES.HRMS_SOP, status: 'ready', requiredPermission: PERMISSIONS.SOP_MANAGE },
          { label: 'Manage Notices', path: '/hrms/notice', icon: Bell, module: MODULES.HRMS_NOTICE, status: 'ready', requiredPermission: PERMISSIONS.NOTICE_MANAGE },
          { label: 'Recruitment', path: '/hrms/recruitment', icon: UserCog, module: MODULES.HRMS_RECRUITMENT, status: 'ready', requiredPermission: PERMISSIONS.RECRUITMENT_VIEW },
          { label: 'Leave Management', path: '/hrms/leave', icon: Plane, module: MODULES.HRMS_LEAVE, status: 'ready', requiredPermission: PERMISSIONS.LEAVE_VIEW_TEAM },
          { label: 'Payroll', path: '/hrms/payroll', icon: Wallet, module: MODULES.HRMS_PAYROLL, status: 'ready', requiredPermission: PERMISSIONS.SALARY_VIEW_ALL },
          { label: 'Attendance', path: '/hrms/attendance', icon: Activity, module: MODULES.HRMS_ATTENDANCE, status: 'ready', requiredPermission: PERMISSIONS.ATTENDANCE_VIEW_TEAM },
          { label: 'Penalty Approval', path: '/hrms/penalty', icon: Shield, module: MODULES.HRMS_PENALTY, status: 'ready', requiredPermission: PERMISSIONS.PENALTY_VIEW_ALL },
          { label: 'Tracking', path: '/hrms/tracking', icon: MonitorSmartphone, module: MODULES.HRMS_TRACKING, status: 'ready', requiredPermission: PERMISSIONS.TRACKING_VIEW },
        ],
      },
      { label: 'Leave', path: '/leave', icon: Plane, module: MODULES.LEAVE, status: 'ready', requiredPermission: PERMISSIONS.LEAVE_VIEW_OWN },
      {
        label: 'Reports',
        icon: BarChart3,
        status: 'ready',
        children: [
          { label: 'Overview', path: '/reports/overview', icon: Eye, module: MODULES.REPORTS_OVERVIEW, status: 'ready', requiredPermission: PERMISSIONS.REPORTS_VIEW },
          { label: 'Daily Reports', path: '/reports/daily', icon: FileText, module: MODULES.REPORTS_DAILY, status: 'planned' },
        ],
      },
    ],
  },
  {
    label: 'Finance & Resources',
    items: [
      {
        label: 'Finance',
        icon: Wallet,
        status: 'ready',
        children: [
          { label: 'Vendors', path: '/finance/vendors', icon: Building2, module: MODULES.FINANCE_VENDORS, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_VENDORS_VIEW },
          { label: 'Purchases', path: '/finance/purchases', icon: Receipt, module: MODULES.FINANCE_PURCHASES, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_PURCHASES_VIEW },
          { label: 'Ledger', path: '/finance/ledger', icon: BookOpen, module: MODULES.FINANCE_LEDGER, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_LEDGER_VIEW },
          { label: 'My Reimbursement', path: '/reimbursement', icon: Wallet, module: MODULES.FINANCE_REIMBURSEMENT_SELF, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_REIMBURSEMENT_REQUEST },
          { label: 'Reimbursements', path: '/finance/reimbursements', icon: Wallet, module: MODULES.FINANCE_REIMBURSEMENTS, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_REIMBURSEMENTS_MANAGE },
          { label: 'Master', path: '/finance/master', icon: Database, module: MODULES.FINANCE_MASTER, status: 'ready', requiredPermission: PERMISSIONS.FINANCE_MASTER },
        ],
      },
      {
        label: 'Assets',
        icon: Package,
        status: 'ready',
        children: [
          { label: 'List', path: '/assets/list', icon: ListTodo, module: MODULES.ASSETS_LIST, status: 'ready', requiredPermission: PERMISSIONS.ASSETS_VIEW },
          { label: 'Overview', path: '/assets/overview', icon: Eye, module: MODULES.ASSETS_OVERVIEW, status: 'ready', requiredPermission: PERMISSIONS.ASSETS_OVERVIEW },
          { label: 'Master', path: '/assets/master', icon: Database, module: MODULES.ASSETS_MASTER, status: 'ready', requiredPermission: PERMISSIONS.ASSETS_MASTER },
          { label: 'ID Management', path: '/assets/id-management', icon: KeyRound, module: MODULES.ASSETS_ID_MANAGEMENT, status: 'ready', requiredPermission: PERMISSIONS.ID_MANAGEMENT_VIEW },
        ],
      },
      {
        label: 'Requirements',
        icon: ClipboardList,
        status: 'ready',
        children: [
          { label: 'List', path: '/requirements/list', icon: ListTodo, module: MODULES.REQUIREMENTS_LIST, status: 'ready', requiredPermission: PERMISSIONS.REQUIREMENTS_VIEW },
          { label: 'Master', path: '/requirements/master', icon: Database, module: MODULES.REQUIREMENTS_MASTER, status: 'ready', requiredPermission: PERMISSIONS.REQUIREMENTS_MASTER },
          { label: 'Overview', path: '/requirements/overview', icon: Eye, module: MODULES.REQUIREMENTS_OVERVIEW, status: 'ready', requiredPermission: PERMISSIONS.REQUIREMENTS_OVERVIEW },
        ],
      },
      {
        label: 'Subscription',
        icon: Repeat,
        status: 'ready',
        children: [
          { label: 'List', path: '/subscription/list', icon: ListTodo, module: MODULES.SUBSCRIPTION_LIST, status: 'ready', requiredPermission: PERMISSIONS.SUBSCRIPTIONS_VIEW },
          { label: 'Overview', path: '/subscription/overview', icon: Eye, module: MODULES.SUBSCRIPTION_OVERVIEW, status: 'ready', requiredPermission: PERMISSIONS.SUBSCRIPTIONS_OVERVIEW },
          { label: 'Master', path: '/subscription/master', icon: Database, module: MODULES.SUBSCRIPTION_MASTER, status: 'ready', requiredPermission: PERMISSIONS.SUBSCRIPTIONS_MASTER },
        ],
      },
    ],
  },
  {
    label: 'Teams & Support',
    items: [
      { label: 'Tech Support', path: '/tech-support', icon: Headphones, module: MODULES.TECH_SUPPORT, status: 'ready', requiredPermission: PERMISSIONS.TECH_SUPPORT_VIEW_OWN },
      { label: 'Support', path: '/support', icon: Headphones, module: MODULES.SUPPORT, status: 'ready' },
      {
        label: 'Roadmap',
        icon: Rocket,
        status: 'planned',
        children: [
          { label: 'Overview', path: '/roadmap/overview', icon: Eye, module: MODULES.ROADMAP_OVERVIEW, status: 'planned' },
          { label: 'Goals', path: '/roadmap/goals', icon: Target, module: MODULES.ROADMAP_GOALS, status: 'planned' },
          { label: 'My Goals', path: '/roadmap/my-goals', icon: Target, module: MODULES.ROADMAP_MY_GOALS, status: 'planned' },
        ],
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        label: 'Roles & Permissions',
        path: '/system/roles',
        icon: Shield,
        module: MODULES.MASTER_ROLES,
        status: 'ready',
        requiredPermission: PERMISSIONS.ROLES_MANAGE,
      },
      {
        label: 'Account',
        icon: UserCog,
        status: 'planned',
        children: [
          { label: 'My Account', path: '/account/me', icon: User, module: MODULES.ACCOUNT_ME, status: 'ready', requiredPermission: PERMISSIONS.PROFILE_VIEW_OWN },
          { label: 'My Attendance', path: '/account/attendance', icon: Activity, module: MODULES.ACCOUNT_ATTENDANCE, status: 'ready', requiredPermission: PERMISSIONS.ATTENDANCE_VIEW_OWN },
          { label: 'Salary Overview', path: '/account/salary', icon: Wallet, module: MODULES.ACCOUNT_SALARY, status: 'ready', requiredPermission: PERMISSIONS.SALARY_VIEW_OWN },
          { label: 'Loan Overview', path: '/account/loans', icon: CreditCard, module: MODULES.ACCOUNT_LOANS, status: 'ready', requiredPermission: PERMISSIONS.LOAN_VIEW_OWN },
          { label: 'Manage Account', path: '/account/manage', icon: Settings, module: MODULES.ACCOUNT_MANAGE, status: 'ready', requiredPermission: PERMISSIONS.PROFILE_EDIT_OWN },
          { label: 'My Assets', path: '/account/assets', icon: Package, module: MODULES.ACCOUNT_ASSETS, status: 'ready', requiredPermission: PERMISSIONS.ASSETS_VIEW_OWN },
          { label: 'My IDs', path: '/account/ids', icon: KeyRound, module: MODULES.ACCOUNT_MY_IDS, status: 'ready', requiredPermission: PERMISSIONS.MY_IDS_VIEW_OWN },
          { label: 'Change Password', path: '/account/password', icon: Lock, module: MODULES.ACCOUNT_PASSWORD, status: 'ready', requiredPermission: PERMISSIONS.PROFILE_EDIT_OWN },
        ],
      },
    ],
  },
];
